import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, Trash2, Filter, AlertCircle, CheckCircle, Edit, RefreshCw } from 'lucide-react';

const PropertyEvaluationManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedEval, setSelectedEval] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [evalToDelete, setEvalToDelete] = useState(null);
  const [filter, setFilter] = useState({ property_type: '', customer_id: '' });

  useEffect(() => {
    fetchEvaluations();
    fetchCustomers();
    fetchLocations();
  }, [filter]);

  const fetchEvaluations = async () => {
    try {
      const params = {};
      if (filter.property_type) params.property_type = filter.property_type;
      if (filter.customer_id) params.customer_id = filter.customer_id;
      
      const response = await axios.get(`${API}/property-evaluations`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEvaluations(response.data);
    } catch (error) {
      toast.error('Failed to fetch evaluations');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleView = async (evalId) => {
    try {
      const response = await axios.get(`${API}/property-evaluations/${evalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEval(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load evaluation');
    }
  };

  const handleEdit = (evaluation) => {
    navigate('/field/property-evaluation', { state: { editData: evaluation } });
  };

  const handleDelete = async (id) => {
    setEvalToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!evalToDelete) return;
    
    try {
      console.log(`Deleting evaluation ID: ${evalToDelete}`);
      await axios.delete(`${API}/property-evaluations/${evalToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluation deleted successfully');
      setShowDeleteDialog(false);
      setEvalToDelete(null);
      fetchEvaluations();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete evaluation');
      setShowDeleteDialog(false);
      setEvalToDelete(null);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : 'Unknown';
  };

  const countIssues = (evaluation) => {
    let count = 0;
    const allItems = [...evaluation.exterior_items, ...evaluation.interior_items];
    allItems.forEach(item => {
      if (item.status === 'needs_attention') count++;
    });
    return count;
  };

  const renderInspectionItems = (title, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 className="font-semibold text-sm mb-2 text-slate-700">{title}</h4>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div 
              key={index} 
              className={`p-3 rounded border ${
                item.status === 'needs_attention' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <p className="text-sm font-medium flex-1">{item.item}</p>
                {item.status === 'needs_attention' ? (
                  <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0 ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                )}
              </div>
              {item.status === 'needs_attention' && item.description && (
                <p className="text-xs text-slate-600 mt-2 pl-2 border-l-2 border-orange-400">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Property Evaluation Management</span>
            <Button onClick={fetchEvaluations} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs">Filter by Property Type</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.property_type}
                onChange={(e) => setFilter({ ...filter, property_type: e.target.value })}
                data-testid="filter-property-type-select"
              >
                <option value="">All Types</option>
                <option value="Bank Branch">Bank Branch</option>
                <option value="Commercial Building">Commercial Building</option>
                <option value="School">School</option>
                <option value="Retail">Retail</option>
                <option value="Mixed Use">Mixed Use</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Filter by Customer</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.customer_id}
                onChange={(e) => setFilter({ ...filter, customer_id: e.target.value })}
                data-testid="filter-customer-select"
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {evaluations.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No evaluations found.</p>
            ) : (
              evaluations.map((evaluation) => {
                const issuesCount = countIssues(evaluation);
                return (
                  <div 
                    key={evaluation.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    style={{ borderLeft: `4px solid ${issuesCount > 0 ? '#F59E0B' : '#6B9F3E'}` }}
                    data-testid="evaluation-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">{evaluation.evaluation_number}</h3>
                          {issuesCount > 0 && (
                            <Badge className="bg-orange-100 text-orange-800">{issuesCount} issue{issuesCount > 1 ? 's' : ''}</Badge>
                          )}
                          {evaluation.forward_to_operations && (
                            <Badge className="bg-red-100 text-red-800">Ops Alert</Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-slate-700">
                          {getCustomerName(evaluation.customer_id)} - {getLocationName(evaluation.location_id)}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          Property: {evaluation.property_type} • Date: {_fmtDate(evaluation.date_of_evaluation)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Evaluator: {evaluation.evaluator_name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(evaluation.id);
                          }}
                          data-testid="view-evaluation-btn"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(evaluation);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(evaluation.id);
                          }}
                          data-testid="delete-evaluation-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluation Details</DialogTitle>
          </DialogHeader>
          {selectedEval && (
            <div className="space-y-6">
              <div className="bg-olive-50 p-4 rounded-lg border-l-4 border-olive-600" style={{ backgroundColor: '#F5F5DC', borderColor: '#BDB76B' }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Evaluation Number</Label>
                    <p className="font-bold">{selectedEval.evaluation_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Property Type</Label>
                    <p className="font-bold">{selectedEval.property_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Customer</Label>
                    <p className="font-medium">{getCustomerName(selectedEval.customer_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <p className="font-medium">{getLocationName(selectedEval.location_id)}</p>
                  </div>
                </div>
              </div>

              {renderInspectionItems('EXTERIOR', selectedEval.exterior_items)}
              {renderInspectionItems('INTERIOR', selectedEval.interior_items)}

              {selectedEval.general_notes && (
                <div>
                  <Label className="text-xs text-slate-500">General Notes</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded">{selectedEval.general_notes}</p>
                </div>
              )}

              {selectedEval.forward_to_operations && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-800">✓ Forwarded to Operations</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this property evaluation? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Evaluation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default PropertyEvaluationManagement;
